import React, { useMemo, useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { databaseEventKind, databaseEventZone, databaseSettingsFields, formatDatabaseSetting } from '../../Utils/database_history_utils';
import ReceivedDetails from './ReceivedDetails';
import SettingsRequestDetails from './SettingsRequestDetails';
import { historyZoomOptions } from './HistoryZoom';
import historyTooltip from './HistoryTooltip';

const kinds = ['settings', 'alarms', 'messages'];
const colors = ['#6bb6e8', '#ef9861', '#9bc31c'];
const useStyles = makeStyles((theme) => ({
  panel: { padding: theme.spacing(2), minWidth: 0 },
  chart: { height: 270, position: 'relative', '& canvas': { touchAction: 'pan-y !important' } },
  row: { cursor: 'pointer', '&:focus': { outline: `2px solid ${theme.palette.primary.main}` } },
  details: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  description: { maxWidth: 550, overflowWrap: 'anywhere' }
}));

// Nearby events share a marker; the dialog keeps every original receipt reachable.
export function eventClusters(rows, range, columns) {
  const width = Math.max(1, (range.to - range.from) / columns);
  const groups = new Map();
  rows.forEach((row) => {
    const kind = databaseEventKind(row);
    const key = kind + ':' + Math.floor((row.received_at - range.from) / width);
    if (!groups.has(key)) groups.set(key, { y: kinds.indexOf(kind), rows: [] });
    groups.get(key).rows.push(row);
  });
  return [...groups.values()].map((group) => ({ ...group,
    x: group.rows.reduce((total, row) => total + row.received_at, 0) / group.rows.length
  })).sort((a, b) => a.x - b.x);
}

export default function Events({ rows, range, filters, zoneLabel, fieldLabel, messageText, onRangeChange }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();
  const [list, setList] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailPage, setDetailPage] = useState(0);
  const matching = useMemo(() => rows.filter((row) =>
    ['message', 'settings_request', 'update_alarms'].includes(row.payload.command) &&
    row.received_at >= range.from && row.received_at < range.to &&
    (filters.eventType === 'all' || filters.eventType === databaseEventKind(row)) &&
    (filters.zone === 'all' || filters.zone === databaseEventZone(row)) &&
    (!filters.actor || row.payload.actor?.username === filters.actor)),
  [rows, range.from, range.to, filters.eventType, filters.zone, filters.actor]);
  const clusters = useMemo(() => eventClusters(matching, range, mobile ? 40 : 100), [matching, range, mobile]);
  const requestFields = useMemo(() => new Map(matching.filter((row) => row.payload.command === 'settings_request')
    .map((row) => [row.id, databaseSettingsFields(row.payload)])), [matching]);
  const kindLabel = (kind) => t('database_event_' + kind);
  const title = (row) => row.payload.command === 'update_alarms' ? t('database_alarm_report') : kindLabel(databaseEventKind(row));
  const actorLabel = ({ actor }) => {
    const kind = ['owner', 'subuser'].includes(actor?.kind) ? t('database_actor_' + actor.kind) : t('unknown');
    return `${actor?.username ?? t('unknown')} (${kind})`;
  };
  const requestSummary = (row) => {
    const fields = requestFields.get(row.id).filter((field) => field.compared);
    const values = fields.slice(0, 2).map((field) =>
      `${fieldLabel(field.path.join('.'))}: ${formatDatabaseSetting(field.path, field.requested, t)}`);
    if (fields.length > 2) values.push(t('database_more_settings', { count: fields.length - 2 }));
    if (!fields.length) values.push(t(requestFields.get(row.id).length ? 'database_request_comparison_unavailable' : 'database_request_no_difference'));
    return [actorLabel(row.payload), ...values].join(' · ');
  };
  const description = (row) => row.payload.command === 'message' ? messageText.get(row.id) :
    row.payload.command === 'settings_request' ? requestSummary(row) : t('database_alarm_report');
  const open = (items) => { setSelected(items); setDetailPage(0); };
  const data = { datasets: kinds.map((kind, index) => ({
    label: kindLabel(kind), data: clusters.filter((group) => group.y === index),
    backgroundColor: colors[index], borderColor: colors[index],
    pointStyle: index === 0 ? 'rectRot' : index === 1 ? 'triangle' : 'circle',
    pointRadius: (context) => Math.min(11, 5 + Math.log2(context.raw?.rows.length ?? 1)), pointHitRadius: 10
  })) };
  const options = {
    responsive: true, maintainAspectRatio: false, animation: false, parsing: false,
    onClick: (_, elements) => { if (elements.length) {
      const { datasetIndex, index } = elements[0]; open(data.datasets[datasetIndex].data[index].rows);
    } },
    scales: {
      x: { type: 'linear', min: range.from, max: range.to, grid: { color: theme.palette.divider },
        ticks: { color: theme.palette.text.secondary, maxTicksLimit: mobile ? 3 : 6, maxRotation: 0,
          callback: (value) => new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) } },
      y: { type: 'linear', min: -0.5, max: 2.5, afterBuildTicks: (scale) => { scale.ticks = kinds.map((_, value) => ({ value })); },
        ticks: { color: theme.palette.text.primary, callback: (value) => kinds[value] ? kindLabel(kinds[value]) : '' },
        grid: { color: theme.palette.divider } }
    },
    plugins: { zoom: historyZoomOptions(onRangeChange), legend: { display: false }, tooltip: { intersect: true, callbacks: {
      title: ([item]) => new Date(item.raw.rows[0].received_at).toLocaleString(),
      label: ({ raw }) => raw.rows.length > 1 ? t('database_event_count', { count: raw.rows.length }) : description(raw.rows[0])
    } } }
  };
  const currentPage = Math.min(page, Math.max(0, Math.ceil(matching.length / 25) - 1));
  return <Paper className={classes.panel}>
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2}>
      <Typography variant="h6">{t('database_events')}</Typography>
      <Box role="group" aria-label={t('database_events')}>
        <Button aria-pressed={!list} color="primary" variant={list ? 'text' : 'contained'} onClick={() => setList(false)}>{t('database_timeline')}</Button>
        <Button aria-pressed={list} color="primary" variant={list ? 'contained' : 'text'} onClick={() => setList(true)}>{t('database_list')}</Button>
      </Box>
    </Box>
    {!matching.length ? <Typography color="textSecondary">{t('database_empty')}</Typography> : list ? <>
      <TableContainer><Table size="small" aria-label={t('database_events')}>
        <TableHead><TableRow>{['database_received_at', 'database_event_type', 'database_axis_zone', 'database_summary'].map((key) =>
          <TableCell key={key}>{t(key)}</TableCell>)}</TableRow></TableHead>
        <TableBody>{matching.slice(currentPage * 25, currentPage * 25 + 25).map((row) => <TableRow key={row.id} hover tabIndex={0}
          className={classes.row} onClick={() => open([row])} onKeyDown={(event) => { if (event.key === 'Enter') open([row]); }}>
          <TableCell>{new Date(row.received_at).toLocaleString()}</TableCell><TableCell>{title(row)}</TableCell>
          <TableCell>{zoneLabel(databaseEventZone(row))}</TableCell><TableCell className={classes.description}>{description(row)}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>
      <Box display="flex" justifyContent="flex-end" mt={1}>
        <Button disabled={!currentPage} onClick={() => setPage(currentPage - 1)}>{t('database_previous_page')}</Button>
        <Button disabled={(currentPage + 1) * 25 >= matching.length} onClick={() => setPage(currentPage + 1)}>{t('database_next_page')}</Button>
      </Box>
    </> : <div className={classes.chart}><Scatter data={data} options={options} plugins={[historyTooltip, zoomPlugin]} role="img" aria-label={t('database_timeline')} /></div>}
    <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth fullScreen={mobile} aria-labelledby="history-event-title">
      <DialogTitle id="history-event-title">{selected?.length === 1 ? title(selected[0]) : t('database_event_count', { count: selected?.length ?? 0 })}</DialogTitle>
      <DialogContent dividers>{selected?.slice(detailPage * 25, detailPage * 25 + 25).map((row) => <Box mb={3} key={row.id}>
        <Typography variant="subtitle2">{new Date(row.received_at).toLocaleString()} · {zoneLabel(databaseEventZone(row))}</Typography>
        <Typography>{row.payload.command === 'settings_request' ? actorLabel(row.payload) : description(row)}</Typography>
        {row.payload.command === 'settings_request' && <Typography variant="caption" color="textSecondary">{t('database_request_unconfirmed')}</Typography>}
        {row.payload.command === 'settings_request' && <SettingsRequestDetails fields={requestFields.get(row.id) ?? databaseSettingsFields(row.payload)} fieldLabel={fieldLabel} />}
        <ReceivedDetails payload={row.payload} className={classes.details} labelKey="database_show_payload" />
      </Box>)}</DialogContent>
      <DialogActions>
        {selected?.length > 25 && <><Button disabled={!detailPage} onClick={() => setDetailPage(detailPage - 1)}>{t('database_previous_page')}</Button>
          <Button disabled={(detailPage + 1) * 25 >= selected.length} onClick={() => setDetailPage(detailPage + 1)}>{t('database_next_page')}</Button></>}
        <Button onClick={() => setSelected(null)}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  </Paper>;
}
